import React, { useEffect, useRef, useState } from 'react';
import { Task } from '../types';
import { executeQuicktask, getAvailableModels } from '../services/api';
import { useTheme } from '../context/ThemeContext';
import { useIdentity, personLabel } from '../context/IdentityContext';

interface QuickTaskComposerProps {
  open: boolean;
  onClose: () => void;
  // Called after a task is successfully created (status in_progress).
  onSubmitted: (task: Task) => void;
  // Quick Tasks page keeps the modal open to fire several in a row; the header
  // shortcut closes and jumps to the new task. Default: close on submit.
  keepOpenAfterSubmit?: boolean;
  // Quick Tasks page shows a running list of what was just submitted.
  showRecentSubmissions?: boolean;
}

// Shared "new quick task" composer used by BOTH the Quick Tasks page and the
// header + shortcut, so there's a single create form (model picker, "running as"
// attribution, Cmd/Ctrl+Enter) instead of two drifting copies. It owns the
// prompt/model/submit state and reads the acting identity from IdentityContext.
const QuickTaskComposer: React.FC<QuickTaskComposerProps> = ({
  open,
  onClose,
  onSubmitted,
  keepOpenAfterSubmit = false,
  showRecentSubmissions = false,
}) => {
  const { theme } = useTheme();
  const { currentPerson } = useIdentity();
  const [prompt, setPrompt] = useState('');
  const [selectedModel, setSelectedModel] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availableModels, setAvailableModels] = useState<string[]>([]);
  const [recentSubmissions, setRecentSubmissions] = useState<string[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Load model options once (only when first opened, to avoid a request on every
  // page that mounts the navbar).
  useEffect(() => {
    if (!open || availableModels.length > 0) return;
    let cancelled = false;
    getAvailableModels()
      .then((models) => {
        if (!cancelled) setAvailableModels(Array.isArray(models) ? models : []);
      })
      .catch(() => {
        /* model picker is optional; ignore load failure */
      });
    return () => {
      cancelled = true;
    };
  }, [open, availableModels.length]);

  // Focus the textarea and clear transient error when the modal opens.
  useEffect(() => {
    if (open) {
      setError(null);
      setTimeout(() => textareaRef.current?.focus(), 0);
    }
  }, [open]);

  const close = () => {
    setPrompt('');
    setError(null);
    onClose();
  };

  const submit = async () => {
    const text = prompt.trim();
    if (!text || isSubmitting) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const task = await executeQuicktask(text, selectedModel || undefined, currentPerson);
      onSubmitted(task);
      if (keepOpenAfterSubmit) {
        setRecentSubmissions((prev) => [text, ...prev].slice(0, 5));
        setPrompt('');
        setTimeout(() => textareaRef.current?.focus(), 0);
      } else {
        close();
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to create quick task.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  const disabled = !prompt.trim() || isSubmitting;

  return (
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        paddingTop: '12vh',
        zIndex: 1000,
      }}
    >
      <div
        style={{
          backgroundColor: theme.surface,
          borderRadius: '12px',
          padding: '24px',
          width: '90%',
          maxWidth: '600px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h2 style={{ color: theme.text, margin: 0, fontSize: '18px' }}>New Quick Task</h2>
          <button
            onClick={close}
            style={{
              background: 'none',
              border: 'none',
              color: theme.textSecondary,
              fontSize: '24px',
              cursor: 'pointer',
              padding: '4px',
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        <p style={{ color: theme.textSecondary, marginBottom: '16px', fontSize: '13px' }}>
          Running as <strong>{personLabel(currentPerson)}</strong>. Describe what to fix or do, then press ⌘/Ctrl+Enter to submit.
        </p>

        {/* Model selection (optional) */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', color: theme.text, fontSize: '14px', marginBottom: '6px' }}>
            Claude Model (optional)
          </label>
          <select
            value={selectedModel || ''}
            onChange={(e) => setSelectedModel(e.target.value || null)}
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: '6px',
              border: `1px solid ${theme.border}`,
              backgroundColor: theme.background,
              color: theme.text,
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            <option value="">System default</option>
            {availableModels.map((model) => (
              <option key={model} value={model}>
                {model}
              </option>
            ))}
          </select>
        </div>

        <textarea
          ref={textareaRef}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              submit();
            } else if (e.key === 'Escape') {
              close();
            }
          }}
          placeholder="e.g., Fix the Create Collection button visibility in E2E tests"
          style={{
            width: '100%',
            minHeight: '90px',
            padding: '12px',
            borderRadius: '8px',
            border: `1px solid ${theme.border}`,
            backgroundColor: theme.background,
            color: theme.text,
            fontSize: '15px',
            resize: 'vertical',
            boxSizing: 'border-box',
          }}
        />

        {error && <div style={{ color: '#ef4444', fontSize: '13px', marginTop: '10px' }}>{error}</div>}

        {showRecentSubmissions && recentSubmissions.length > 0 && (
          <div style={{ marginTop: '16px' }}>
            <div style={{ color: theme.textSecondary, fontSize: '12px', marginBottom: '8px' }}>Recently submitted:</div>
            {recentSubmissions.map((sub, i) => (
              <div
                key={i}
                style={{
                  color: theme.textSecondary,
                  fontSize: '13px',
                  padding: '4px 8px',
                  backgroundColor: theme.background,
                  borderRadius: '4px',
                  marginBottom: '4px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {sub}
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: '12px', marginTop: '20px', justifyContent: 'flex-end' }}>
          <button
            onClick={close}
            style={{
              padding: '10px 20px',
              borderRadius: '6px',
              border: `1px solid ${theme.border}`,
              backgroundColor: theme.surface,
              color: theme.text,
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            {keepOpenAfterSubmit ? 'Close' : 'Cancel'}
          </button>
          <button
            onClick={submit}
            disabled={disabled}
            style={{
              padding: '10px 20px',
              borderRadius: '6px',
              border: 'none',
              backgroundColor: disabled ? theme.border : theme.primary,
              color: '#fff',
              cursor: disabled ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: 600,
            }}
          >
            {isSubmitting ? 'Submitting…' : 'Create (⌘+Enter)'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuickTaskComposer;
